import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { AdminLayout } from './AdminLayout';

const ACTION_META = {
  'auth.login':           { label: 'Login',              color: 'bg-green-500/15 text-green-400' },
  'auth.password_change': { label: 'Passwort geändert',  color: 'bg-amber-500/15 text-amber-400' },
  'auth.password_reset':  { label: 'Passwort-Reset',     color: 'bg-amber-500/15 text-amber-400' },
  'provider.create':      { label: 'Anbieter erstellt',  color: 'bg-cyan-500/15 text-cyan-400' },
  'provider.update':      { label: 'Anbieter bearbeitet',color: 'bg-blue-500/15 text-blue-400' },
  'provider.delete':      { label: 'Anbieter gelöscht',  color: 'bg-red-500/15 text-red-400' },
  'team.create':          { label: 'Nutzer erstellt',    color: 'bg-cyan-500/15 text-cyan-400' },
  'team.delete':          { label: 'Nutzer gelöscht',    color: 'bg-red-500/15 text-red-400' },
  'system.setup':         { label: 'System-Setup',       color: 'bg-purple-500/15 text-purple-400' },
};

const FILTER_OPTIONS = [
  { value: '', label: 'Alle Aktionen' },
  { value: 'auth.login', label: 'Logins' },
  { value: 'provider.create', label: 'Anbieter erstellt' },
  { value: 'provider.update', label: 'Anbieter bearbeitet' },
  { value: 'provider.delete', label: 'Anbieter gelöscht' },
  { value: 'team.create', label: 'Nutzer erstellt' },
  { value: 'team.delete', label: 'Nutzer gelöscht' },
  { value: 'auth.password_change', label: 'Passwort geändert' },
  { value: 'auth.password_reset', label: 'Passwort-Reset' },
];

const PAGE_SIZE = 25;

function formatDate(str) {
  if (!str) return '—';
  try {
    const d = new Date(str);
    return d.toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return str; }
}

function ActionBadge({ action }) {
  const meta = ACTION_META[action] || { label: action, color: 'bg-slate-700 text-slate-400' };
  return (
    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${meta.color}`}>
      {meta.label}
    </span>
  );
}

export function AdminAudit() {
  const { authHeaders, API } = useAdminAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        ...(filter ? { action: filter } : {}),
      });
      const res = await axios.get(`${API}/admin/audit?${params}`, { headers: authHeaders() });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [API, authHeaders, page, filter]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 md:p-8">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-white">Audit-Protokoll</h1>
            <p className="text-sm text-slate-500 mt-0.5">{total} Einträge gesamt</p>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={filter}
              onChange={e => { setFilter(e.target.value); setPage(0); }}
              data-testid="audit-filter"
              className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-slate-300 focus:outline-none focus:border-cyan-500 transition-colors"
            >
              {FILTER_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <button
              onClick={load}
              data-testid="audit-refresh"
              className="p-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-400 hover:text-white hover:border-slate-600 transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        <div className="bg-[#0f172a] border border-slate-800 rounded-xl overflow-hidden" data-testid="audit-table">
          {items.length === 0 && !loading ? (
            <div className="py-16 text-center text-slate-500 text-sm">
              Keine Einträge vorhanden
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-800">
                    <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Zeitpunkt</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Nutzer</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Aktion</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Objekt</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Details</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/60">
                  {items.map(item => (
                    <tr key={item.id} className="hover:bg-slate-800/30 transition-colors" data-testid="audit-row">
                      <td className="px-5 py-3 text-slate-400 whitespace-nowrap font-mono text-xs">
                        {formatDate(item.created_at)}
                      </td>
                      <td className="px-5 py-3">
                        <div className="text-white font-medium">{item.user_name || '—'}</div>
                        <div className="text-slate-500 text-xs">{item.user_email}</div>
                      </td>
                      <td className="px-5 py-3">
                        <ActionBadge action={item.action} />
                      </td>
                      <td className="px-5 py-3 text-slate-300 max-w-[160px] truncate" title={item.target}>
                        {item.target || '—'}
                      </td>
                      <td className="px-5 py-3 text-slate-500 text-xs max-w-[200px] truncate" title={item.details}>
                        {item.details || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-slate-500">
              Seite {page + 1} von {totalPages}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                data-testid="audit-prev"
                className="p-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-400 hover:text-white disabled:opacity-40 transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                data-testid="audit-next"
                className="p-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-400 hover:text-white disabled:opacity-40 transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
